import { useId, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { MEASUREMENT_UNIT } from '../sbSharedRefineMenuConstants.js';
import {
    FILTER_STATS_WIDTH,
    isDimensionFilter,
    isTableWidthFilter,
} from '../sbSharedRefineMenuHelpers.ts';

// components
import { SbSharedRefineMenuInputRange } from './SbSharedRefineMenuInputRange.jsx';
import { SbSharedRefineMenuDimensionFacet } from '../SbSharedRefineMenuDimensionFacet/SbSharedRefineMenuDimensionFacet.jsx';
import { useMeasurementUnit } from '../../useMeasurementUnit.ts';
import { SbSharedRefineMenuDimensionUnitRadioSelect } from '../SbSharedRefineMenuDimensionUnitRadioSelect/SbSharedRefineMenuDimensionUnitRadioSelect.tsx';
import { SbSharedRefineMenuFilterTableSize } from '../SbSharedRefineMenuFilters/SbSharedRefineMenuFilterTableSize.tsx';

import { type SbSharedRefineMenuInputRangeDimensions_itemSearch$key } from './__generated__/SbSharedRefineMenuInputRangeDimensions_itemSearch.graphql.ts';
import { type SbSharedRefineMenuInputRangeDimensions_filters$key } from './__generated__/SbSharedRefineMenuInputRangeDimensions_filters.graphql.ts';
import { type SbSharedRefineMenuInputRangeDimensions_user$key } from './__generated__/SbSharedRefineMenuInputRangeDimensions_user.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuInputRangeDimensions_itemSearch on ItemSearchQueryConnection {
        ...SbSharedRefineMenuInputRange_itemSearch
        ...useMeasurementUnit_itemSearch
        ...SbSharedRefineMenuDimensionUnitRadioSelect_itemSearch
        ...SbSharedRefineMenuFilterTableSize_itemSearch
    }
`;
const filtersFragment = graphql`
    fragment SbSharedRefineMenuInputRangeDimensions_filters on SearchBrowseFilter
    @relay(plural: true) {
        ...SbSharedRefineMenuFilterTableSize_filters
        name
        localizedFilterName
        values {
            urlLabel
            linkReference
            stats {
                key
                values
            }
        }
    }
`;
const userFragment = graphql`
    fragment SbSharedRefineMenuInputRangeDimensions_user on User {
        ...useMeasurementUnit_user
        ...SbSharedRefineMenuDimensionUnitRadioSelect_user
        ...SbSharedRefineMenuFilterTableSize_user
    }
`;

type Props = {
    itemSearch: SbSharedRefineMenuInputRangeDimensions_itemSearch$key;
    filters: SbSharedRefineMenuInputRangeDimensions_filters$key;
    user: SbSharedRefineMenuInputRangeDimensions_user$key;
    applyOnBlur?: boolean;
    showInvalidRangeErrorMessage?: boolean;
    size?: 'small' | 'medium' | 'large';
};

export const SbSharedRefineMenuInputRangeDimensions: FC<Props> = ({
    itemSearch: itemSearchRef,
    filters: filtersRef,
    user: userRef,
    size,
    applyOnBlur,
    showInvalidRangeErrorMessage,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const filters = useFragment(filtersFragment, filtersRef);
    const user = useFragment(userFragment, userRef);
    let dimensionFilters = filters.filter(
        dimensionFilter => dimensionFilter.name && isDimensionFilter(dimensionFilter.name)
    );
    const tableWidthRanges =
        filters.find(
            dimensionFilter => dimensionFilter.name && isTableWidthFilter(dimensionFilter.name)
        ) || null;

    if (tableWidthRanges) {
        dimensionFilters = dimensionFilters.sort((a, b) =>
            (a?.name || '') < (b?.name || '') ? 1 : -1
        );
    }

    const [measurementUnit] = useMeasurementUnit({ user, itemSearch });
    const htmlIdBase = useId();
    return (
        <>
            <SbSharedRefineMenuDimensionUnitRadioSelect
                user={user}
                itemSearch={itemSearch}
                selectedMeasurementUnit={measurementUnit}
            />
            {dimensionFilters.map(dimensionFilter => {
                const { localizedFilterName, name } = dimensionFilter;
                const id = `${htmlIdBase}-${name}`;
                const showTableWidthRanges = tableWidthRanges && name === FILTER_STATS_WIDTH;
                return (
                    <SbSharedRefineMenuDimensionFacet
                        id={id}
                        key={name}
                        title={localizedFilterName || ''}
                    >
                        <>
                            {showTableWidthRanges && (
                                <SbSharedRefineMenuFilterTableSize
                                    itemSearch={itemSearch}
                                    filters={tableWidthRanges}
                                    user={user}
                                />
                            )}
                            <SbSharedRefineMenuInputRange
                                ariaDescribedBy={id}
                                size={size}
                                filterName={name}
                                filter={dimensionFilter}
                                applyOnBlur={applyOnBlur}
                                statKey={measurementUnit}
                                additionalFilter={MEASUREMENT_UNIT}
                                itemSearch={itemSearch}
                                showInvalidRangeErrorMessage={showInvalidRangeErrorMessage}
                                disregardAvailableRange={showTableWidthRanges}
                            />
                        </>
                    </SbSharedRefineMenuDimensionFacet>
                );
            })}
        </>
    );
};
